// Sieger-Pose (Design §11.2): the match winner's tank with its pose, behind the name in the
// final standings, and in the Optik-Shop. Drawn in a box of 5 × 3 tank radii, looping.
import { CONFETTI, hash, skullPath, spikes } from './looks.js';
import { type Parts, STANDARD_PARTS } from './parts.js';
import { type Constants, drawTank } from './render.js';

/** Size of the pose box in tank radii. */
export const POSE_W = 5;
export const POSE_H = 3;
/** Where the tank sits in the box, in tank radii from the top left. */
export const POSE_TANK_X = 2.1;
export const POSE_TANK_Y = 2.05;
const POSE_MS = 2500; // one loop
const TANK_SCALE = 0.8; // the tank a little smaller, so the pose fits above it
const GOLD = '#ffd34d';
const GOLD_EDGE = '#8a6a00';

const ease = (q: number) => (q < 0.5 ? 2 * q * q : 1 - 2 * (1 - q) * (1 - q));
const easeOutBack = (q: number) => 1 + 2.7 * (q - 1) ** 3 + 1.7 * (q - 1) ** 2;

/**
 * Pose `id` (0 = just the tank) of a tank in `color` with `paint` and `parts`, filling a box
 * `w` canvas units wide from the origin.
 */
export function drawPose(
  ctx: CanvasRenderingContext2D,
  id: number,
  w: number,
  color: string,
  c: Pick<Constants, 'tankRadius' | 'barrelLength'>,
  paint = 0,
  parts: Parts = STANDARD_PARTS,
  now = 0,
  colorLook = 0,
): void {
  const r = c.tankRadius;
  const p = (now % POSE_MS) / POSE_MS;
  const [x, y] = [POSE_TANK_X * r, POSE_TANK_Y * r];
  const tip = c.barrelLength * TANK_SCALE;
  ctx.save();
  ctx.scale(w / (POSE_W * r), w / (POSE_W * r));
  const body = (angle = 0, s = 1) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(TANK_SCALE * s, TANK_SCALE * s);
    drawTank(ctx, new Float32Array([0, 0, angle, 1, 1]), 0, color, c, false, undefined, paint, true, parts, now, false, 0, colorLook);
    ctx.restore();
  };

  switch (id) {
    case 48: { // Medaille: a gold medal on a ribbon in the player colour swings above the tank
      body();
      ctx.save();
      ctx.translate(x, 0);
      ctx.rotate(Math.sin(p * Math.PI * 2) * 0.12);
      const at = 0.95 * r; // middle of the medal below the hook
      ctx.lineCap = 'butt';
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(side * 0.32 * r, 0);
        ctx.lineTo(side * 0.07 * r, at - 0.28 * r);
        ctx.strokeStyle = color;
        ctx.lineWidth = 0.17 * r;
        ctx.stroke();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 0.05 * r;
        ctx.stroke();
      }
      const disc = () => {
        ctx.beginPath();
        ctx.arc(0, at, 0.34 * r, 0, Math.PI * 2);
      };
      disc();
      ctx.fillStyle = GOLD;
      ctx.fill();
      ctx.save();
      ctx.clip();
      glint(ctx, 0, at, r, p);
      ctx.restore();
      disc();
      ctx.strokeStyle = GOLD_EDGE;
      ctx.lineWidth = 0.06 * r;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, at, 0.25 * r, 0, Math.PI * 2);
      ctx.lineWidth = 0.03 * r;
      ctx.stroke();
      spikes(ctx, 0, at, 5, 0.17 * r, 0.07 * r, -Math.PI / 2);
      ctx.fillStyle = '#e0a800';
      ctx.fill();
      ctx.restore();
      sparkle(ctx, x + 0.25 * r, 0.7 * r, r, p);
      break;
    }
    case 49: // Konfetti-Regen: confetti raining down on the tank
      body();
      for (let k = 0; k < 22; k++) {
        const [seed, speed] = [hash(k, 1), hash(k, 2)];
        const q = (now / (1800 + speed * 900) + seed) % 1;
        ctx.save();
        ctx.translate((0.2 + seed * 4.6) * r + Math.sin(q * 6 + k) * 0.2 * r, -0.2 * r + q * 3.4 * r);
        ctx.rotate(seed * 6 + q * 10);
        ctx.scale(1, Math.cos(q * 12 + k)); // tumbling
        ctx.fillStyle = CONFETTI[k % CONFETTI.length];
        ctx.fillRect(-0.11 * r, -0.05 * r, 0.22 * r, 0.1 * r);
        ctx.restore();
      }
      break;
    case 50: { // Pirouette: the tank spins once round itself with a hop, dust whirling
      const q = Math.min(1, p / 0.6);
      const hop = Math.sin(Math.PI * q);
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * Math.PI * 2 + ease(q) * Math.PI * 3;
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * 1.05 * r, y + Math.sin(a) * 0.75 * r, (0.12 + 0.1 * hop) * r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(150, 140, 120, ${0.5 * hop})`;
        ctx.fill();
      }
      body(ease(q) * Math.PI * 2, 1 + 0.08 * hop);
      break;
    }
    case 51: // Feuerwerk: three rockets in the player colour rise and burst
      body();
      for (const [k, [bx, by]] of [[0.9, 0.85], [2.6, 0.65], [4.1, 0.9]].entries()) {
        const t = p - k * 0.2;
        if (t < 0) continue;
        const [fx, fy] = [bx * r, by * r];
        if (t < 0.2) {
          const u = 1 - (1 - t / 0.2) ** 2;
          const [rx, ry] = [x + (fx - x) * u, y - 0.3 * r + (fy - y + 0.3 * r) * u];
          ctx.strokeStyle = 'rgba(255, 220, 150, 0.6)';
          ctx.lineWidth = 0.06 * r;
          ctx.beginPath();
          ctx.moveTo(rx - (fx - x) * 0.15, ry - (fy - y) * 0.15);
          ctx.lineTo(rx, ry);
          ctx.stroke();
          ctx.fillStyle = '#fff';
          ctx.fillRect(rx - 0.05 * r, ry - 0.05 * r, 0.1 * r, 0.1 * r);
        } else if (t < 0.6) {
          const u = (t - 0.2) / 0.4;
          ctx.globalAlpha = 1 - u;
          for (let j = 0; j < 12; j++) {
            const a = (j / 12) * Math.PI * 2 + k;
            const d = 0.62 * r * Math.sqrt(u);
            ctx.fillStyle = j % 3 ? color : '#fff';
            ctx.beginPath();
            ctx.arc(fx + Math.cos(a) * d, fy + Math.sin(a) * d + u * u * 0.25 * r, 0.07 * r, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
      }
      break;
    case 52: { // Spotlight: everything dark, a light cone swings onto the winner
      body();
      const q = ease(Math.min(1, p / 0.35));
      const [sx, tx, ty] = [2.5 * r, 4.6 * r + (x - 4.6 * r) * q, y + 0.2 * r];
      const cone = () => {
        ctx.moveTo(sx - 0.1 * r, -0.4 * r);
        ctx.lineTo(tx - 1.15 * r, ty);
        ctx.ellipse(tx, ty, 1.15 * r, 0.6 * r, 0, Math.PI, 0, true);
        ctx.lineTo(sx + 0.1 * r, -0.4 * r);
        ctx.closePath();
      };
      // The dark fades out towards the edges, so it shows no box around the pose.
      const dark = ctx.createRadialGradient(x, y - 0.5 * r, 0, x, y - 0.5 * r, 2.4 * r);
      dark.addColorStop(0.5, 'rgba(8, 10, 18, 0.8)');
      dark.addColorStop(1, 'rgba(8, 10, 18, 0)');
      ctx.beginPath();
      ctx.rect(0, 0, POSE_W * r, POSE_H * r);
      cone();
      ctx.fillStyle = dark;
      ctx.fill('evenodd');
      const light = ctx.createLinearGradient(0, -0.4 * r, 0, ty);
      light.addColorStop(0, 'rgba(255, 250, 215, 0.05)');
      light.addColorStop(1, 'rgba(255, 250, 215, 0.3)');
      ctx.beginPath();
      cone();
      ctx.fillStyle = light;
      ctx.fill();
      break;
    }
    case 53: { // Piratenflagge: a mast rises from the turret, a black flag with skull and bones waves
      body();
      const q = ease(Math.min(1, p / 0.3));
      const top = y - q * 1.75 * r;
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 0.09 * r;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, top);
      ctx.stroke();
      const [fw, fh] = [1.1 * r * q, 0.55 * r];
      const wave = (i: number) => Math.sin(now / 150 - i * 0.8) * 0.08 * r * (i / 8);
      ctx.beginPath();
      for (let i = 0; i <= 8; i++) ctx.lineTo(x + (fw * i) / 8, top + wave(i));
      for (let i = 8; i >= 0; i--) ctx.lineTo(x + (fw * i) / 8, top + fh + wave(i));
      ctx.closePath();
      ctx.fillStyle = '#16161a';
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 0.04 * r;
      ctx.stroke();
      if (q > 0.6) {
        // Skull and crossed bones in the middle of the cloth, riding its wave.
        const [sx, sy] = [x + fw * 0.5, top + fh * 0.45 + wave(4)];
        const s = 0.13 * r;
        ctx.save();
        ctx.globalAlpha = (q - 0.6) / 0.4;
        ctx.strokeStyle = '#f1ece0';
        ctx.fillStyle = '#f1ece0';
        ctx.lineWidth = 0.05 * r;
        ctx.lineCap = 'round';
        ctx.beginPath();
        for (const d of [-1, 1]) {
          ctx.moveTo(sx - 0.24 * r, sy + 0.06 * r + d * 0.1 * r);
          ctx.lineTo(sx + 0.24 * r, sy + 0.06 * r - d * 0.1 * r);
        }
        ctx.stroke();
        ctx.translate(sx, sy - 0.02 * r);
        skullPath(ctx, s);
        ctx.fill();
        ctx.fillStyle = '#16161a';
        ctx.beginPath();
        ctx.arc(-s * 0.36, -s * 0.12, s * 0.24, 0, Math.PI * 2);
        ctx.moveTo(s * 0.6, -s * 0.12);
        ctx.arc(s * 0.36, -s * 0.12, s * 0.24, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      ctx.beginPath();
      ctx.arc(x, top, 0.09 * r, 0, Math.PI * 2);
      ctx.fillStyle = GOLD;
      ctx.fill();
      break;
    }
    case 54: { // Salut: the raised barrel fires three harmless flares into the air
      const angle = -0.45;
      body(angle);
      const [mx, my] = [x + Math.cos(angle) * tip, y + Math.sin(angle) * tip];
      for (const [k, a] of [-0.95, -1.2, -1.45].entries()) {
        const t = (p - k * 0.2) / 0.45;
        if (t < 0 || t >= 1) continue;
        const at = (u: number) => [mx + Math.cos(a) * 2 * r * u, my + Math.sin(a) * 2 * r * u + 0.9 * r * u * u];
        if (t < 0.2) {
          ctx.beginPath();
          ctx.arc(mx, my, 0.25 * r * (1 - t / 0.2), 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.fill();
        }
        for (let j = 4; j >= 0; j--) {
          const [fx, fy] = at(Math.max(0, t - j * 0.04));
          const glow = j === 0 && t > 0.75 ? 1 + (t - 0.75) * 6 : 1; // pops at the end
          ctx.globalAlpha = (1 - j / 5) * (t > 0.75 ? (1 - t) / 0.25 : 1);
          ctx.beginPath();
          ctx.arc(fx, fy, 0.1 * r * glow * (1 - j * 0.15), 0, Math.PI * 2);
          ctx.fillStyle = j === 0 ? '#fff' : color;
          ctx.shadowColor = color;
          ctx.shadowBlur = j === 0 ? 0.3 * r : 0;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      }
      break;
    }
    case 55: { // Geldregen: gold coins rain down, spinning, and pile up at the tank's feet, glinting
      body();
      const fade = p > 0.88 ? (1 - p) / 0.12 : 1; // the pile goes before the next shower
      for (let k = 0; k < 16; k++) {
        const t = (p - hash(k, 4) * 0.55) / 0.22; // falling 0..1, then lying
        if (t < 0) continue;
        const cx = (0.3 + hash(k, 3) * 4.4) * r;
        const ground = (2.62 + hash(k, 5) * 0.3) * r;
        const lying = t >= 1;
        const cy = lying ? ground : -0.2 * r + (ground + 0.2 * r) * t * t;
        const wide = lying ? 1 : Math.max(0.2, Math.abs(Math.cos(t * 9 + k))); // spinning on the way down
        ctx.globalAlpha = lying ? fade : 1;
        ctx.beginPath();
        ctx.ellipse(cx, cy, 0.14 * r * wide, lying ? 0.07 * r : 0.14 * r, 0, 0, Math.PI * 2);
        ctx.fillStyle = GOLD;
        ctx.fill();
        ctx.strokeStyle = GOLD_EDGE;
        ctx.lineWidth = 0.03 * r;
        ctx.stroke();
        if (lying && k % 4 === 0) sparkle(ctx, cx + 0.05 * r, cy - 0.04 * r, r * 0.6, (p + hash(k, 6) * 0.4) % 1);
      }
      ctx.globalAlpha = 1;
      break;
    }
    case 279: { // Burnout: the tank spins on the spot, black rings burn into the ground, smoke billows
      ctx.strokeStyle = `rgba(20, 20, 20, ${0.15 + 0.35 * Math.min(1, p * 2)})`;
      ctx.lineWidth = 0.16 * r;
      ctx.beginPath();
      for (const rr of [0.5, 0.82]) {
        ctx.moveTo(x + rr * r, y);
        ctx.arc(x, y, rr * r, 0, Math.PI * 2);
      }
      ctx.stroke();
      const spin = now / 240;
      body(spin + Math.sin(now / 35) * 0.05);
      for (let k = 0; k < 8; k++) {
        const q = (now / 900 + k / 8) % 1;
        const a = (k * Math.PI) / 4 + spin;
        const d = (0.7 + 0.9 * q) * r;
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7 - q * 0.6 * r, (0.15 + 0.32 * q) * r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(205, 205, 200, ${0.45 * (1 - q)})`;
        ctx.fill();
      }
      break;
    }
    case 280: { // Blitz-Aura: the tank glows blue, bolts crackle round it
      const flick = Math.floor(now / 70); // a new crackle every 70 ms
      const glow = ctx.createRadialGradient(x, y, 0.3 * r, x, y, 1.5 * r);
      glow.addColorStop(0, 'rgba(130, 195, 255, 0.6)');
      glow.addColorStop(1, 'rgba(60, 120, 255, 0)');
      ctx.globalAlpha = 0.7 + 0.3 * hash(flick, 9);
      ctx.fillStyle = glow;
      ctx.fillRect(x - 1.5 * r, y - 1.5 * r, 3 * r, 3 * r);
      ctx.globalAlpha = 1;
      body();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (let k = 0; k < 5; k++) {
        if (hash(flick, k) < 0.3) continue; // not all at once
        const a = hash(flick, k + 10) * Math.PI * 2;
        const [ux, uy] = [Math.cos(a), Math.sin(a)];
        const [bx, by] = [x + ux * 0.75 * r, y + uy * 0.6 * r];
        ctx.moveTo(bx, by);
        for (let j = 1; j <= 4; j++) {
          const off = j === 4 ? 0 : (hash(flick + k, j) - 0.5) * 0.3 * r;
          ctx.lineTo(bx + ux * 0.2 * r * j - uy * off, by + uy * 0.2 * r * j + ux * off);
        }
      }
      ctx.strokeStyle = 'rgba(80, 150, 255, 0.55)';
      ctx.lineWidth = 0.16 * r;
      ctx.stroke();
      ctx.strokeStyle = '#e6f4ff';
      ctx.lineWidth = 0.05 * r;
      ctx.stroke();
      break;
    }
    case 56: { // Roboter (bots only): a little robot pops out of the turret and waves
      body();
      const rise = easeOutBack(Math.min(1, p / 0.25));
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, POSE_W * r, y); // hidden below the hatch
      ctx.clip();
      ctx.translate(x, y + 0.12 * r + (1 - rise) * 1.4 * r);
      robot(ctx, r, color, now);
      ctx.restore();
      break;
    }
    default:
      body();
  }
  ctx.restore();
}

/** The bots' robot standing on the origin, about one tank radius tall, waving its right arm. */
function robot(ctx: CanvasRenderingContext2D, r: number, color: string, now: number): void {
  const [metal, edge] = ['#b8c2cc', '#46515c'];
  ctx.lineWidth = 0.05 * r;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const limb = (x: number, y: number, angle: number) => {
    const [hx, hy] = [x + Math.cos(angle) * 0.36 * r, y + Math.sin(angle) * 0.36 * r];
    ctx.strokeStyle = edge;
    ctx.lineWidth = 0.09 * r;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    ctx.lineWidth = 0.05 * r;
    ctx.beginPath();
    ctx.arc(hx, hy, 0.08 * r, 0, Math.PI * 2);
    ctx.fillStyle = metal;
    ctx.fill();
    ctx.stroke();
  };
  limb(-0.25 * r, -0.38 * r, Math.PI * 0.62); // left arm down
  limb(0.25 * r, -0.38 * r, -1.2 + Math.sin(now / 140) * 0.5); // right arm up, waving
  const part = (x: number, y: number, w: number, h: number, fill: string) => {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 0.07 * r);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.stroke();
  };
  part(-0.25 * r, -0.44 * r, 0.5 * r, 0.44 * r, metal); // body
  ctx.beginPath();
  ctx.arc(0, -0.24 * r, 0.08 * r, 0, Math.PI * 2);
  ctx.fillStyle = color; // chest light in the tank's colour
  ctx.fill();
  ctx.stroke();
  part(-0.22 * r, -0.84 * r, 0.44 * r, 0.34 * r, metal); // head
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(side * 0.09 * r, -0.7 * r, 0.055 * r, 0, Math.PI * 2);
    ctx.fillStyle = '#3cf0ff';
    ctx.fill();
  }
  ctx.strokeStyle = edge;
  ctx.beginPath();
  ctx.moveTo(-0.07 * r, -0.58 * r);
  ctx.lineTo(0.07 * r, -0.58 * r);
  ctx.moveTo(0, -0.84 * r);
  ctx.lineTo(0, -0.98 * r); // antenna
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, -1.02 * r, 0.05 * r, 0, Math.PI * 2);
  ctx.fillStyle = Math.floor(now / 400) % 2 ? '#ff4b4b' : '#7a2020'; // blinking
  ctx.fill();
}

/** A white band sweeping over gold (clip to the gold first), once per loop. */
function glint(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, p: number): void {
  const q = (p - 0.4) / 0.3;
  if (q < 0 || q > 1) return;
  const gx = x + (q * 2 - 1) * r;
  ctx.beginPath();
  ctx.moveTo(gx - 0.12 * r, y + r);
  ctx.lineTo(gx + 0.08 * r, y + r);
  ctx.lineTo(gx + 0.38 * r, y - r);
  ctx.lineTo(gx + 0.18 * r, y - r);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
  ctx.fill();
}

/** A four-pointed white sparkle, blinking up late in the loop. */
function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, p: number): void {
  const q = (p - 0.7) / 0.25;
  if (q < 0 || q > 1) return;
  spikes(ctx, x, y, 4, 0.25 * r * Math.sin(q * Math.PI), 0.05 * r, Math.PI / 4);
  ctx.fillStyle = '#fff';
  ctx.fill();
}
